import { tierById } from './catalog';
import { quote } from './pricing';
import { createDemoSponsorState } from './seed';
import type { SponsorStorage } from './storage';
import type {
  AdStatus, PlaceOrderInput, RegisterInput, SponsorAccount, SponsorAd,
  SponsorNotice, SponsorOrder, SponsorPayment, SponsorState,
} from './types';

interface SponsorStoreOptions {
  storage: SponsorStorage;
  now?: () => number;
  createInitialState?: () => SponsorState;
}

/** Demo actions only; the future API must enforce payments and authorization. */
export function createSponsorStore({
  storage,
  now: clock = Date.now,
  createInitialState = () => createDemoSponsorState(clock()),
}: SponsorStoreOptions) {
  const DAY_MS = 86_400_000;
  const createId = (prefix: string) => `${prefix}-${globalThis.crypto.randomUUID()}`;
  const createOrderNumber = () => 'LB-' + (10000 + globalThis.crypto.getRandomValues(new Uint32Array(1))[0] % 90000);
  const toIsoString = (time: number) => new Date(time).toISOString();
  let state = storage.read() ?? createInitialState();
  let persistenceFailed = false;
  const listeners = new Set<() => void>();
  let unsubscribeStorage: (() => void) | undefined;
  const emit = () => listeners.forEach((listener) => listener());

  function commit(next: SponsorState) {
    state = next;
    persistenceFailed = !storage.write(next);
    emit();
  }

  function subscribe(listener: () => void) {
    listeners.add(listener);
    if (!unsubscribeStorage) {
      unsubscribeStorage = storage.subscribe(() => {
        state = storage.read() ?? createInitialState();
        persistenceFailed = false;
        emit();
      });
      if (!persistenceFailed) state = storage.read() ?? state;
    }
    return () => {
      listeners.delete(listener);
      if (listeners.size === 0) {
        unsubscribeStorage?.();
        unsubscribeStorage = undefined;
      }
    };
  }

  function notify(s: SponsorState, accountId: string, text: string): SponsorNotice[] {
    // Real email/SMS delivery belongs to backend jobs; this records demo notices only.
    return [{ id: createId('ntc'), at: new Date(clock()).toISOString(), accountId, text }, ...s.notices].slice(0, 100);
  }

  function updateOrder(id: string, fn: (o: SponsorOrder) => SponsorOrder, message?: string) {
    const order = state.orders.find((o) => o.id === id);
    if (!order) return;
    const next = fn(order);
    commit({
      ...state,
      orders: state.orders.map((o) => (o.id === id ? next : o)),
      notices: message ? notify(state, order.accountId, message) : state.notices,
    });
  }

  function registerAccount(input: RegisterInput): SponsorAccount {
    const account: SponsorAccount = { ...input, id: createId('acc'), status: 'registered', createdAt: new Date(clock()).toISOString() };
    commit({ ...state, accounts: [...state.accounts, account], currentAccountId: account.id });
    return account;
  }

  function signOut() {
    commit({ ...state, currentAccountId: null });
  }

  function placeOrder(input: PlaceOrderInput): SponsorOrder {
    const accountId = state.currentAccountId;
    const account = state.accounts.find((account) => account.id === accountId);
    if (!accountId || !account || account.status === 'rejected') throw new Error('An eligible sponsor account is required.');
    const tier = tierById(input.tier);
    const q = quote(tier, input.stopIndex, input.periodMonths, input.segment);
    const now = clock();
    const bank = input.method === 'bank';
    const multi = tier.id !== 'starter';
    const ads: SponsorAd[] = [
      { id: createId('ad'), title: input.bannerName || `${input.company} banner`, type: 'Banner', showsIn: multi ? 'All apps' : tier.apps[0], status: 'checking', share: multi ? 0.46 : 1 },
      ...(multi
        ? [
            { id: createId('ad'), title: `${input.company} feed post`, type: 'Feed post', showsIn: 'IDI, Survey', status: 'checking' as AdStatus, share: 0.31 },
            { id: createId('ad'), title: `${input.company} survey`, type: 'Sponsored survey', showsIn: 'Survey app', status: 'checking' as AdStatus, share: 0.15 },
            { id: createId('ad'), title: 'Sponsored reward', type: 'Sponsored reward', showsIn: 'All apps', status: 'checking' as AdStatus, share: 0.08 },
          ]
        : []),
      ...(tier.id === 'premium'
        ? [{ id: createId('ad'), title: `${input.company} launch`, type: 'Full-screen ad', showsIn: 'All apps', status: 'checking' as AdStatus, share: 0 }]
        : []),
    ];
    const order: SponsorOrder = {
      id: createId('ord'), no: createOrderNumber(), accountId,
      company: input.company, contact: input.contact, phone: input.phone,
      tier: tier.id, stopIndex: input.stopIndex, periodMonths: input.periodMonths, months: q.months,
      segment: input.segment, method: input.method,
      list: q.list, paid: q.paid, vat: q.vat, total: q.total,
      status: bank ? 'waiting_payment' : 'in_review',
      createdAt: toIsoString(now), start: toIsoString(now), end: toIsoString(now + q.months * 30 * DAY_MS),
      link: input.link, provinces: input.provinces, ads,
    };
    const payment: SponsorPayment = {
      id: createId('pay'), date: toIsoString(now), accountId, orderId: order.id,
      item: `${tier.name} package · ${q.months} month${q.months > 1 ? 's' : ''}`, no: order.no, amount: q.total,
      status: bank ? 'waiting' : 'paid',
    };
    commit({
      ...state,
      orders: [...state.orders, order],
      payments: [...state.payments, payment],
      notices: notify(state, accountId, bank ? `Invoice ${order.no} sent. Pay by bank transfer within 7 days.` : `Payment for ${order.no} received. OPS is checking your ads.`),
    });
    return order;
  }

  function resubmitAd(orderId: string, fileName: string) {
    const order = state.orders.find((order) => order.id === orderId);
    if (!order || order.status !== 'needs_changes' || order.accountId !== state.currentAccountId) return;
    updateOrder(orderId, (o) => ({
      ...o,
      status: 'in_review',
      changeNote: undefined,
      // The new file replaces the ad OPS sent back.
      ads: o.ads.map((a) => (a.status === 'needs_changes' ? { ...a, title: fileName || a.title, status: 'checking' as AdStatus } : a)),
    }), 'New ad sent. OPS is checking it.');
  }

  function buyPoints(accountId: string, item: string, amount: number, points: number) {
    const account = state.accounts.find((account) => account.id === accountId);
    if (!account || account.status === 'rejected' || accountId !== state.currentAccountId) throw new Error('An eligible sponsor account is required.');
    if (!Number.isFinite(amount) || amount <= 0 || !Number.isSafeInteger(points) || points <= 0) throw new Error('A positive amount and whole points are required.');
    const nextBalance = (state.extraPoints[accountId] ?? 0) + points;
    if (!Number.isSafeInteger(nextBalance)) throw new Error('Point balance exceeds the supported range.');
    commit({
      ...state,
      extraPoints: { ...state.extraPoints, [accountId]: nextBalance },
      payments: [...state.payments, { id: createId('pay'), date: new Date(clock()).toISOString(), accountId, item, no: createOrderNumber(), amount, status: 'paid' }],
    });
  }

  function confirmTransfer(orderId: string) {
    const order = state.orders.find((o) => o.id === orderId);
    if (!order || order.status !== 'waiting_payment') return;
    commit({
      ...state,
      orders: state.orders.map((o) => (o.id === orderId ? { ...o, status: 'in_review' } : o)),
      payments: state.payments.map((p) => (p.orderId === orderId ? { ...p, status: 'paid' } : p)),
      notices: notify(state, order.accountId, `Payment for ${order.no} confirmed. OPS is checking your ads.`),
    });
  }

  /** A package starts when OPS approves it, unless its planned start is still ahead. */
  function getRunDates(o: SponsorOrder) {
    const start = Math.max(clock(), new Date(o.start).getTime());
    return { start: toIsoString(start), end: toIsoString(start + o.months * 30 * DAY_MS) };
  }

  function approveOrder(orderId: string) {
    const order = state.orders.find((o) => o.id === orderId);
    if (!order || (order.status !== 'in_review' && order.status !== 'needs_changes')) return;
    const account = state.accounts.find((account) => account.id === order.accountId);
    if (!account || account.status === 'rejected') return;
    commit({
      ...state,
      accounts: state.accounts.map((a) => (a.id === order.accountId ? { ...a, status: 'verified' } : a)),
      orders: state.orders.map((o) =>
        o.id === orderId
          ? { ...o, status: 'live', changeNote: undefined, ...getRunDates(o), ads: o.ads.map((a) => ({ ...a, status: a.type === 'Full-screen ad' ? 'scheduled' : 'live' })) }
          : o,
      ),
      notices: notify(state, order.accountId, `Your ads for ${order.no} are live.`),
    });
  }

  function requestChanges(orderId: string, note: string) {
    const order = state.orders.find((order) => order.id === orderId);
    if (!order || (order.status !== 'in_review' && order.status !== 'needs_changes')) return;
    updateOrder(orderId, (o) => ({
      ...o,
      status: 'needs_changes',
      changeNote: note,
      ads: o.ads.map((a, i) => (i === 0 ? { ...a, status: 'needs_changes' } : a)),
    }), `OPS asked for a change: ${note}`);
  }

  function rejectAccount(accountId: string) {
    commit({
      ...state,
      accounts: state.accounts.map((a) => (a.id === accountId ? { ...a, status: 'rejected' } : a)),
      notices: notify(state, accountId, 'Your sponsor account was not approved. OPS will call you.'),
    });
  }

  function endOrder(orderId: string) {
    updateOrder(orderId, (o) => ({ ...o, status: 'ended', ads: o.ads.map((a) => ({ ...a, status: 'ended' })) }), 'Your package has ended.');
  }

  function resetSponsorDemo() {
    commit(createInitialState());
  }

  return {
    getSnapshot: () => state, subscribe,
    registerAccount, signOut, placeOrder, resubmitAd, buyPoints,
    confirmTransfer, approveOrder, requestChanges, rejectAccount, endOrder, resetSponsorDemo,
  };
}
